import React, { useEffect, useMemo, useRef, useState } from 'react';

import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { useTheme } from '@mui/material/styles';

const HIDE_DELAY_MS = 150;
const ARROW_SIZE = 8;

let hostSetState:
  | React.Dispatch<
      React.SetStateAction<{
        open: boolean;
        anchorEl: HTMLElement | null;
        content: React.ReactNode;
        interactive: boolean;
      }>
    >
  | undefined;

let closeTimer: ReturnType<typeof setTimeout> | null = null;
let currentAnchor: HTMLElement | null = null;

function cancelHide() {
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
}

function scheduleHide() {
  cancelHide();
  closeTimer = setTimeout(() => {
    currentAnchor = null;
    hostSetState?.((prev) => ({ ...prev, open: false }));
    closeTimer = null;
  }, HIDE_DELAY_MS);
}

export function RootTooltipHost() {
  const [tooltipState, setTooltipState] = useState({
    open: false,
    anchorEl: null as HTMLElement | null,
    content: null as React.ReactNode,
    interactive: false,
  });
  useEffect(() => {
    hostSetState = setTooltipState;
    return () => {
      if (hostSetState === setTooltipState) hostSetState = undefined;
    };
  }, []);

  const [placement, setPlacement] = useState('top-start');
  const theme = useTheme();

  // Non-interactive: inverted label style (matches MUI Tooltip default)
  // Interactive: card/popover style (matches InfoTooltipLabel); both show the callout arrow
  const { interactive } = tooltipState;
  const bg = theme.palette.text.primary;
  const fg = theme.palette.background.paper;

  const offset = interactive ? 4 : ARROW_SIZE;

  const modifiers = useMemo(
    () => [
      { name: 'offset', options: { offset: [0, offset] } },
      { name: 'flip', options: { fallbackPlacements: ['top-end', 'bottom-start', 'bottom-end'] } },
      { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
      {
        name: 'captureNewPlacement',
        enabled: true,
        phase: 'afterWrite' as const,
        fn: ({ state }: { state: { placement: string } }) => {
          setPlacement((prev) => (prev !== state.placement ? state.placement : prev));
        },
      },
    ],
    [offset],
  );

  const isTop = placement.startsWith('top');
  const isEnd = placement.endsWith('end');

  // Covers a mouse user clicking into interactive content (e.g. a button inside the tooltip),
  // which focuses it natively — Escape closes and returns focus to the anchor.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      cancelHide();
      const { anchorEl } = tooltipState;
      currentAnchor = null;
      setTooltipState((prev) => ({ ...prev, open: false }));
      anchorEl?.focus();
    }
  };

  return (
    <Popper
      open={tooltipState.open}
      anchorEl={tooltipState.anchorEl}
      placement='top-start'
      modifiers={modifiers}
      style={{ zIndex: theme.zIndex.tooltip }}
    >
      <Paper
        onMouseEnter={interactive ? cancelHide : undefined}
        onMouseLeave={interactive ? scheduleHide : undefined}
        onFocus={interactive ? cancelHide : undefined}
        onBlur={interactive ? scheduleHide : undefined}
        onKeyDown={handleKeyDown}
        sx={{
          position: 'relative',
          p: 1,
          maxWidth: 600,
          pointerEvents: interactive ? 'auto' : 'none',
          backgroundColor: bg,
          color: fg,
          borderRadius: 'lg',
          ...(interactive && {
            border: '1px solid',
            borderColor: 'border.light',
            boxShadow: theme.shadows[12],
          }),
          ...theme.typography.paragraphRegular,
        }}
      >
        {typeof tooltipState.content === 'string' ? (
          <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{tooltipState.content}</span>
        ) : (
          tooltipState.content
        )}
        <span
          style={{
            position: 'absolute',
            width: 0,
            height: 0,
            ...(isTop
              ? {
                  bottom: -ARROW_SIZE,
                  borderLeft: `${ARROW_SIZE}px solid transparent`,
                  borderRight: `${ARROW_SIZE}px solid transparent`,
                  borderTop: `${ARROW_SIZE}px solid ${bg}`,
                }
              : {
                  top: -ARROW_SIZE,
                  borderLeft: `${ARROW_SIZE}px solid transparent`,
                  borderRight: `${ARROW_SIZE}px solid transparent`,
                  borderBottom: `${ARROW_SIZE}px solid ${bg}`,
                }),
            ...(isEnd ? { right: 12 } : { left: 12 }),
          }}
        />
      </Paper>
    </Popper>
  );
}

export const RootTooltip = {
  show(anchorEl: HTMLElement, content: React.ReactNode, { interactive = false }: { interactive?: boolean } = {}) {
    cancelHide();
    currentAnchor = anchorEl;
    hostSetState?.({ open: true, anchorEl, content, interactive });
  },
  hide() {
    cancelHide();
    currentAnchor = null;
    hostSetState?.((prev) => ({ ...prev, open: false }));
  },
  /** Hides only if `anchorEl` is still the anchor of the currently shown tooltip. */
  hideIfOwnedBy(anchorEl: HTMLElement): void {
    if (currentAnchor === anchorEl) this.hide();
  },
  scheduleHide,
  cancelHide,
};

/**
 * Wires a cell's hover-triggered RootTooltip: shows `content` on mouse-enter, schedules a
 * hide on mouse-leave, and hides on unmount if this cell's tooltip is still the one shown
 * (avoids closing another cell's tooltip when virtualization unmounts this one mid-hover).
 * Pass a falsy `content` to suppress the tooltip entirely (e.g. for rows where there's
 * nothing to show). Interactive content can be a function that receives `hideTooltip`.
 */
type RootTooltipControls = {
  hideTooltip: () => void;
};

type RootTooltipContent = React.ReactNode | ((controls: RootTooltipControls) => React.ReactNode);

export function useRootTooltip(
  content: RootTooltipContent,
  { interactive = false }: { interactive?: boolean } = {},
): {
  onMouseEnter: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave: () => void;
} {
  const anchorRef = useRef<HTMLElement | null>(null);

  useEffect(
    () => () => {
      if (anchorRef.current) RootTooltip.hideIfOwnedBy(anchorRef.current);
    },
    [],
  );

  const hideTooltip = () => {
    if (anchorRef.current) RootTooltip.hideIfOwnedBy(anchorRef.current);
  };

  return {
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      const resolvedContent = typeof content === 'function' ? content({ hideTooltip }) : content;
      if (resolvedContent) {
        anchorRef.current = e.currentTarget;
        RootTooltip.show(e.currentTarget, resolvedContent, { interactive });
      }
    },
    onMouseLeave: scheduleHide,
  };
}
